import { ADVANCED_CARE_PROGRAM } from "@/core/constants";
import type { ConsentPayloadDTO, ConsentResponseDTO, PatientConsent, UUID } from "@/core/types";
import { showToast } from "@/providers/ToastProvider";
import { useOptimisticMutation } from "@/lib/hooks/useOptimisticMutation";

import { appendPatientConsent } from "../queryFunctions/appendPatientConsent";
import { patientConsentsKeys } from "../keyFactory";

type UseOptOutConsentProps = {
  patientId: UUID;
  patientName: string;
  sortKey: string;
};

// Lean hook for the Advanced Care Program opt-out flow — the response body (ConsentResponseDTO)
// carries no display data, so the optimistic write patches the cached PatientConsent by sortKey
// directly instead of merging server data in.
export function useOptOutConsent({ patientId, patientName, sortKey }: UseOptOutConsentProps) {
  return useOptimisticMutation<PatientConsent[], ConsentPayloadDTO, ConsentResponseDTO>({
    getErrorMessage: () => "Something went wrong. Please reload the page and try again.",
    // BE needs a few seconds to update the consents table — invalidating immediately would
    // revert the UI to the stale (pre-opt-out) state.
    invalidateOnSettled: false,
    meta: { component: "OptOutButton", consentType: ADVANCED_CARE_PROGRAM, patientId, patientName },
    mutationFn: appendPatientConsent,
    onSuccess: () =>
      showToast([
        {
          heading: `${patientName} successfully opted out of the Advanced Care Program.`,
          variant: "success",
        },
      ]),
    optimisticStateResolver: (old, _payload) =>
      old.map((consent) =>
        consent.sort_key === sortKey ? { ...consent, consent_response: false } : consent,
      ),
    queryKey: patientConsentsKeys.list(patientId),
  });
}
